import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const STAGE_TONE = {
  production_ready: "bg-slate-100 text-slate-600", assigned: "bg-blue-100 text-blue-700", in_preparation: "bg-indigo-100 text-indigo-700",
  internal_review: "bg-violet-100 text-violet-700", corrections: "bg-orange-100 text-orange-700", final_review: "bg-violet-100 text-violet-700",
  client_review: "bg-cyan-100 text-cyan-700", ready_to_file: "bg-teal-100 text-teal-700", filed: "bg-emerald-100 text-emerald-700", completed: "bg-emerald-100 text-emerald-700",
};
const LIFECYCLE_TONE = {
  production_ready: "bg-slate-100 text-slate-600",
  in_production:    "bg-indigo-100 text-indigo-700",
  waiting_on_client:"bg-amber-100 text-amber-700",
  awaiting_government: "bg-blue-100 text-blue-700",
  completed:        "bg-emerald-100 text-emerald-700",
};
const IR_TYPES = ["missing_document", "missing_tax_form", "missing_signature", "clarification_required", "additional_income_information",
  "business_information", "foreign_tax_information", "foreign_bank_information", "crypto_information", "rental_information",
  "identity_verification", "general_question", "other"];

// Workflow phase groupings (UI only — not persisted)
const WORKFLOW_PHASES = [
  { key: "documents",      label: "Documents",       stages: ["production_ready", "assigned"] },
  { key: "preparation",    label: "Preparation",     stages: ["in_preparation"] },
  { key: "quality_review", label: "Quality Review",  stages: ["internal_review", "corrections", "final_review"] },
  { key: "client_approval",label: "Client Approval", stages: ["client_review"] },
  { key: "filing",         label: "Filing",          stages: ["ready_to_file", "filed", "completed"] },
];

function phaseForStage(stage) {
  return WORKFLOW_PHASES.find((ph) => ph.stages.includes(stage));
}

function Card({ title, icon, children, action }) {
  const Ic = Icons[icon] || Icons.Box;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3"><h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Ic className="h-4 w-4 text-slate-400" />{title}</h2>{action}</div>
      {children}
    </div>
  );
}
const Stat = ({ label, value }) => <div><div className="text-[11px] text-slate-400">{label}</div><div className="text-sm font-medium text-slate-700 capitalize">{value ?? "—"}</div></div>;

// Draft upload stages: which stages allow uploading a draft return
const DRAFT_UPLOAD_STAGES = ["in_preparation", "internal_review", "corrections", "final_review", "client_review", "ready_to_file"];
const REVIEW_STATUS_TONE = {
  pending:  "bg-slate-100 text-slate-500",
  running:  "bg-blue-100 text-blue-700",
  findings_ready: "bg-amber-100 text-amber-700",
  failed:   "bg-rose-100 text-rose-700",
  staff_disposed: "bg-emerald-100 text-emerald-700",
};
const FINDING_SEVERITY_TONE = {
  high: "text-rose-700 bg-rose-50 border-rose-200",
  medium: "text-amber-700 bg-amber-50 border-amber-200",
  low: "text-slate-600 bg-slate-50 border-slate-200",
  info: "text-blue-600 bg-blue-50 border-blue-200",
};
const DISPOSITIONS = [
  { value: "resolved", label: "Resolved" },
  { value: "no_action_needed", label: "No Action" },
  { value: "correct_return", label: "Correct Return" },
  { value: "request_information_from_client", label: "Request Info from Client" },
];

function UnitCard({ unit, pid, prod, onDone, busy, setBusy }) {
  const phase = phaseForStage(unit.stage);
  const hasSetupIssue = !!unit.setup_issue;
  const isComplete = unit.stage === "completed";
  const canAdvance = !isComplete && !unit.waiting_status && unit.next_stage;
  const canUploadDraft = DRAFT_UPLOAD_STAGES.includes(unit.stage);

  const [review, setReview] = useState(null);
  const [showFindings, setShowFindings] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [disposeBusy, setDisposeBusy] = useState("");
  const fileRef = useState(() => { const r = { current: null }; return r; })[0];

  const loadReview = useCallback(() => {
    api.get(`/production/records/${pid}/units/${unit.id}/draft-review/latest`)
      .then((r) => setReview(r.data.review))
      .catch(() => {});
  }, [pid, unit.id]);

  useEffect(() => { loadReview(); }, [loadReview]);

  // Poll while review is running/pending
  useEffect(() => {
    if (!review || !["pending", "running"].includes(review.status)) return;
    const interval = setInterval(loadReview, 3000);
    return () => clearInterval(interval);
  }, [review, loadReview]);

  const advanceUnit = () => {
    setBusy(`unit-${unit.id}`);
    api.post(`/production/records/${pid}/units/${unit.id}/advance`)
      .then((r) => { toast.success(r.data?.message || "Advanced"); onDone(); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setBusy(""));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files accepted for draft returns."); return; }
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("client_id", prod.client_id || "");
      form.append("engagement_id", prod.engagement_id || "");
      form.append("destination_type", "engagement");
      form.append("destination_id", prod.engagement_id || "");
      form.append("document_type", "us_individual_tax_return");
      form.append("category", "tax_return");
      if (unit.tax_year) form.append("tax_year", unit.tax_year);
      form.append("production_unit_id", unit.id);
      const uploadResp = await api.post("/documents-intel/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const docId = uploadResp.data?.document_id || uploadResp.data?.id;
      if (!docId) throw new Error("Upload did not return a document_id.");
      await api.post(`/production/records/${pid}/units/${unit.id}/draft-review`, { document_id: docId });
      toast.success("Draft uploaded — Allison analysis started.");
      loadReview();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Upload failed.");
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const disposeFind = async (findingId, disposition) => {
    if (!review) return;
    setDisposeBusy(findingId);
    try {
      const resp = await api.post(`/allison/draft-review/${review.id}/dispose`, { finding_id: findingId, disposition });
      setReview(resp.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDisposeBusy(""); }
  };

  const reviewStatusLabel = { pending: "Analysis Queued", running: "Allison Analysing…", findings_ready: "Findings Ready", failed: "Analysis Failed", staff_disposed: "All Findings Disposed" };

  return (
    <div className={`rounded-xl border p-4 ${isComplete ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
         data-testid={`prod-unit-${unit.id}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{unit.label}</span>
            {unit.tax_year && <span className="text-xs text-slate-400">TY {unit.tax_year}</span>}
            {hasSetupIssue && (
              <span title={unit.setup_issue} className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                <Icons.AlertTriangle className="h-3 w-3" />Setup issue
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${STAGE_TONE[unit.stage] || "bg-slate-100 text-slate-500"}`}>
              {unit.stage_label || unit.stage}
            </span>
            {phase && <span className="text-[11px] text-slate-400">Phase: {phase.label}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isComplete && <Icons.CheckCircle2 className="h-5 w-5 text-emerald-500" />}
          {canAdvance && (
            <button
              onClick={advanceUnit}
              disabled={busy === `unit-${unit.id}`}
              data-testid={`prod-unit-advance-${unit.id}`}
              className="inline-flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg px-3 py-1.5"
            >
              <Icons.ChevronRight className="h-3.5 w-3.5" />
              {unit.next_stage_label ? `→ ${unit.next_stage_label}` : "Advance"}
            </button>
          )}
          {unit.waiting_status && (
            <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 flex items-center gap-1">
              <Icons.Clock className="h-3 w-3" />Waiting
            </span>
          )}
        </div>
      </div>

      {/* Draft Return row */}
      {canUploadDraft && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            {review ? (
              <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border ${REVIEW_STATUS_TONE[review.status] || "bg-slate-100 text-slate-500"}`}>
                {review.status === "running" && <Icons.Loader2 className="h-3 w-3 animate-spin" />}
                {reviewStatusLabel[review.status] || review.status}
                {review.finding_count > 0 && ` (${review.finding_count})`}
              </span>
            ) : (
              <span className="text-[11px] text-slate-400">No draft uploaded</span>
            )}
            {review?.status === "findings_ready" && (
              <button
                onClick={() => setShowFindings((s) => !s)}
                data-testid={`prod-unit-findings-toggle-${unit.id}`}
                className="text-[11px] text-blue-600 hover:underline"
              >
                {showFindings ? "Hide findings" : "Review findings"}
              </button>
            )}
            {review?.status === "staff_disposed" && (
              <button onClick={() => setShowFindings((s) => !s)} className="text-[11px] text-slate-500 hover:underline">
                {showFindings ? "Hide" : "View disposed"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={(el) => { fileRef.current = el; }}
              type="file" accept=".pdf" className="hidden"
              data-testid={`prod-unit-draft-input-${unit.id}`}
              onChange={handleFileSelect}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadBusy}
              data-testid={`prod-unit-upload-draft-${unit.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg px-2.5 py-1 disabled:opacity-50"
            >
              {uploadBusy ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Upload className="h-3 w-3" />}
              {review ? "Re-upload Draft" : "Upload Draft Return"}
            </button>
          </div>
        </div>
      )}

      {/* Findings panel */}
      {showFindings && review?.findings?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`prod-unit-findings-panel-${unit.id}`}>
          {review.findings.map((f) => (
            <div key={f.id} className={`rounded-lg border p-3 text-xs ${FINDING_SEVERITY_TONE[f.severity] || "bg-slate-50 border-slate-200 text-slate-600"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{f.headline}</div>
                  {f.detail && <div className="mt-0.5 text-[11px] opacity-80">{f.detail}</div>}
                </div>
                <span className="shrink-0 text-[10px] uppercase tracking-wide opacity-60">{f.severity}</span>
              </div>
              {/* Disposition */}
              {f.disposition ? (
                <div className="mt-2 text-[11px] opacity-70 flex items-center gap-1">
                  <Icons.CheckCircle2 className="h-3 w-3" />{f.disposition.replace(/_/g, " ")}
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {DISPOSITIONS.map((d) => (
                    <button
                      key={d.value}
                      onClick={() => disposeFind(f.id, d.value)}
                      disabled={disposeBusy === f.id}
                      data-testid={`prod-finding-dispose-${f.id}-${d.value}`}
                      className="text-[10px] px-2 py-0.5 rounded border border-current opacity-70 hover:opacity-100 disabled:opacity-40"
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductionWorkspace() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tp, setTp] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get(`/production/records/${pid}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), [pid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.client?.id) api.get(`/tax-profile/${d.client.id}/summary`).then((r) => setTp(r.data)).catch(() => {});
  }, [d?.client?.id]);

  const act = async (fn, key) => { setBusy(key); try { await fn(); await load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); } };
  const advance = () => act(() => api.post(`/production/records/${pid}/advance`).then(() => toast.success("Advanced")), "advance");
  const review = (action, note) => act(() => api.post(`/production/records/${pid}/review`, { action, note }).then(() => toast.success("Done")), "review");
  const filing = (body) => act(() => api.post(`/production/records/${pid}/filing`, body).then(() => toast.success("Filing recorded")), "filing");
  const complete = () => act(() => api.post(`/production/records/${pid}/complete`).then(() => toast.success("Completed")), "complete");
  const toggleTask = (tid) => act(() => api.post(`/production/tasks/${tid}/toggle`), "task");

  if (!d) return <div className="flex justify-center py-24"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;
  const p = d.production, ds = d.document_summary, os = d.organizer_state;
  const openIrs = d.information_requests.filter((i) => !["completed", "satisfied"].includes(i.status));
  const units = p.units || [];
  const hasUnits = units.length > 0;

  // Header status badge: lifecycle_status for multi-unit records, stage for single
  const statusBadgeLabel = hasUnits
    ? (p.lifecycle_status_label || p.stage_label)
    : (p.waiting_label || p.stage_label);
  const statusBadgeTone = hasUnits
    ? (LIFECYCLE_TONE[p.lifecycle_status] || "bg-slate-100 text-slate-600")
    : (STAGE_TONE[p.stage] || "bg-slate-100");

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="production-workspace">
      {/* Compact reference navigation */}
      <nav className="flex items-center gap-2 text-sm text-slate-400" aria-label="Production workspace navigation">
        <button
          onClick={() => navigate("/production?tab=queue")}
          className="flex items-center gap-1 text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="prod-back-to-queue"
        >
          <Icons.ArrowLeft className="h-3.5 w-3.5" />Back to queue
        </button>
        <span aria-hidden="true">·</span>
        <button
          onClick={() => navigate(`/client-workspace/${d.client?.id}`)}
          className="hover:text-slate-700 transition-colors"
          data-testid="prod-nav-client-workspace"
        >
          Client Workspace
        </button>
        <span aria-hidden="true">·</span>
        <button
          onClick={() => navigate(`/client-workspace/${d.client?.id}?tab=documents`)}
          className="hover:text-slate-700 transition-colors"
          data-testid="prod-nav-documents"
        >
          Documents
        </button>
      </nav>

      {/* header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">Production Workspace</p>
            <h1 className="text-xl font-bold text-slate-900">{d.client?.full_name} · {p.engagement_number}</h1>
            <p className="text-sm text-slate-500 mt-0.5 capitalize">{(p.service_type || "").replace(/_/g, " ")} · FY {p.filing_year || "—"}</p>
          </div>
          <div className="flex items-center gap-2">
            {tp && (
              <button onClick={() => navigate(`/client-workspace/${d.client.id}?tab=tax`)}
                data-testid="prod-tax-profile-badge" title="Open full Tax Profile"
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer transition-colors">
                <Icons.ClipboardList className="h-3.5 w-3.5" />Tax Profile {tp.completion ?? 0}% · {tp.confidence ?? 0}% verified
              </button>
            )}
            <span data-testid="prod-ws-stage" className={`text-xs px-2.5 py-1 rounded-full ${statusBadgeTone}`}>{statusBadgeLabel}</span>
            {/* Global Advance: disabled for multi-unit records (use per-unit advance instead) */}
            {p.stage !== "completed" && !hasUnits && (
              <button onClick={advance} disabled={!!p.waiting_status || busy === "advance"} title={p.waiting_status ? "Resolve open information requests first" : ""} data-testid="prod-advance" className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg px-3 py-1.5"><Icons.ChevronRight className="h-4 w-4" />Advance</button>
            )}
          </div>
        </div>
        {/* pipeline (shown for single-unit records only to avoid confusion with multi-unit) */}
        {!hasUnits && (
          <div className="mt-4 flex items-center gap-1 overflow-x-auto pb-1" data-testid="prod-pipeline">
            {d.pipeline.map(([key, label], i) => (
              <div key={key} className="flex items-center shrink-0">
                <span className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ${p.stage === key ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"}`}>{label}</span>
                {i < d.pipeline.length - 1 && <Icons.ChevronRight className="h-3 w-3 text-slate-300 mx-0.5" />}
              </div>
            ))}
          </div>
        )}
        {/* lifecycle phase strip for multi-unit records */}
        {hasUnits && (
          <div className="mt-4 flex items-center gap-1 overflow-x-auto pb-1" data-testid="prod-lifecycle-strip">
            {WORKFLOW_PHASES.map((ph, i) => {
              const active = units.some((u) => ph.stages.includes(u.stage));
              const done   = units.every((u) => !ph.stages.includes(u.stage) || u.stage === "completed");
              return (
                <div key={ph.key} className="flex items-center shrink-0">
                  <span className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ${active ? "bg-blue-600 text-white" : done ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{ph.label}</span>
                  {i < WORKFLOW_PHASES.length - 1 && <Icons.ChevronRight className="h-3 w-3 text-slate-300 mx-0.5" />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {p.waiting_status && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-sm text-amber-800 flex items-center gap-2" data-testid="prod-waiting-banner">
          <Icons.Clock className="h-4 w-4" />{p.waiting_label} — engagement stays in Production. It resumes automatically when all open information requests are satisfied.
        </div>
      )}

      {/* ── Production Units Panel (Phase 2) ─────────────────────────────────── */}
      {hasUnits && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="prod-units-panel">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4">
            <Icons.Layers className="h-4 w-4 text-slate-400" />
            Production Units
            <span className="ml-auto text-[11px] font-normal text-slate-400">
              {units.filter((u) => u.stage === "completed").length}/{units.length} complete
            </span>
          </h2>
          <div className="space-y-3">
            {units.map((unit) => (
              <UnitCard key={unit.id} unit={unit} pid={pid} prod={p} onDone={load} busy={busy} setBusy={setBusy} />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Team" icon="Users" action={<button onClick={() => setDialog({ type: "assign" })} data-testid="prod-assign-btn" className="text-[11px] text-blue-600 hover:underline">Assign</button>}>
          <div className="grid grid-cols-2 gap-3"><Stat label="Preparer" value={p.preparer} /><Stat label="Reviewer" value={p.reviewer} /><Stat label="Manager" value={p.manager} /><Stat label="Team" value={p.team_id} /></div>
        </Card>
        <Card title="Risk & Complexity" icon="Gauge">
          <div className="grid grid-cols-2 gap-3"><Stat label="Priority" value={p.priority} /><Stat label="Complexity" value={p.complexity_label} /><Stat label="Risk Level" value={p.risk_level} /><Stat label="Days in Stage" value={p.days_in_stage} /></div>
        </Card>
        <Card title="Organizer" icon="ClipboardList">
          {os ? (<>
            <div className="flex items-center gap-2"><div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${os.progress}%` }} /></div><span className="text-xs text-slate-500">{os.progress}%</span></div>
            <p className="text-xs text-slate-400 mt-1.5">{os.required_remaining} required remaining · {d.organizer?.status?.replace(/_/g, " ")}</p>
          </>) : <p className="text-xs text-slate-400">No organizer data.</p>}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Documents" icon="Files">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Requested", ds.requested], ["Approved", ds.approved], ["Missing", ds.missing], ["Awaiting", ds.uploaded], ["Revision", ds.needs_revision], ["Waived", ds.waived]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-lg py-2"><div className="text-lg font-bold text-slate-800">{v}</div><div className="text-[10px] text-slate-400">{l}</div></div>
            ))}
          </div>
        </Card>
        <Card title="Tax Profile" icon="Receipt">
          {d.tax_profile ? (<div className="grid grid-cols-2 gap-3">
            <Stat label="Filing Status" value={d.tax_profile.filing_status} /><Stat label="Foreign Accounts" value={d.tax_profile.foreign_accounts ? "Yes" : "No"} />
            <Stat label="Business Owner" value={d.tax_profile.business_owner ? "Yes" : "No"} /><Stat label="FBAR Max" value={d.tax_profile.fbar_max_value} /></div>) : <p className="text-xs text-slate-400">No tax profile.</p>}
        </Card>
        <Card title="Allison Insights" icon="Bot">
          <div className="space-y-1.5 text-xs">
            {Object.entries(d.penny).map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-400 capitalize">{k.replace(/_/g, " ")}</span><span className="text-slate-300 italic">{v}</span></div>)}
          </div>
        </Card>
      </div>

      {/* review / filing / complete actions */}
      <Card title="Workflow Actions" icon="Workflow">
        <div className="flex flex-wrap gap-2">
          {!hasUnits && <button onClick={() => review("approve_for_client_review", null)} data-testid="prod-review-approve" className="inline-flex items-center gap-1.5 text-sm border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-lg px-3 py-2"><Icons.Check className="h-4 w-4" />Approve for Client Review</button>}
          {!hasUnits && <button onClick={() => setDialog({ type: "review", action: "request_corrections" })} data-testid="prod-review-corrections" className="inline-flex items-center gap-1.5 text-sm border border-orange-200 text-orange-700 hover:bg-orange-50 rounded-lg px-3 py-2"><Icons.RotateCcw className="h-4 w-4" />Request Corrections</button>}
          <button onClick={() => setDialog({ type: "review", action: "escalate" })} className="inline-flex items-center gap-1.5 text-sm border border-rose-200 text-rose-700 hover:bg-rose-50 rounded-lg px-3 py-2"><Icons.AlertTriangle className="h-4 w-4" />Escalate</button>
          <button onClick={() => setDialog({ type: "filing" })} data-testid="prod-filing-btn" className="inline-flex items-center gap-1.5 text-sm border border-teal-200 text-teal-700 hover:bg-teal-50 rounded-lg px-3 py-2"><Icons.Send className="h-4 w-4" />File / Deliver</button>
          <button onClick={complete} disabled={busy === "complete"} data-testid="prod-complete-btn" className="inline-flex items-center gap-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-2"><Icons.CheckCircle2 className="h-4 w-4" />Mark Completed</button>
        </div>
        {hasUnits && <p className="mt-2 text-[11px] text-slate-400 flex items-center gap-1"><Icons.Info className="h-3 w-3" />Advance each return independently using the buttons in Production Units above.</p>}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={`Information Requests (${openIrs.length} open)`} icon="MessageCircleQuestion" action={<button onClick={() => setDialog({ type: "ir" })} data-testid="prod-ir-create" className="text-[11px] text-blue-600 hover:underline">+ New Request</button>}>
          {d.information_requests.length === 0 ? <p className="text-xs text-slate-400">No information requests.</p> : (
            <div className="space-y-2" data-testid="prod-ir-list">
              {d.information_requests.map((ir) => (
                <div key={ir.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2">
                  <div className="min-w-0"><div className="text-sm text-slate-700 truncate">{ir.title}</div><div className="text-[11px] text-slate-400 capitalize">{(ir.request_type || "").replace(/_/g, " ")} · {ir.priority}</div></div>
                  {["completed", "satisfied"].includes(ir.status) ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> :
                    <button onClick={() => act(() => api.post(`/production/information-requests/${ir.id}/complete`), "ir")} data-testid={`prod-ir-complete-${ir.id}`} className="text-[11px] text-blue-600 hover:underline shrink-0">Complete</button>}
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card title="Production Tasks" icon="ListTodo" action={<button onClick={() => setDialog({ type: "task" })} data-testid="prod-task-create" className="text-[11px] text-blue-600 hover:underline">+ Task</button>}>
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="prod-task-list">
            {d.tasks.map((t) => (
              <button key={t.id} onClick={() => toggleTask(t.id)} data-testid={`prod-task-${t.id}`} className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-slate-50">
                <Icons.CheckCircle2 className={`h-4 w-4 shrink-0 ${t.status === "completed" ? "text-emerald-500" : "text-slate-300"}`} />
                <span className={`text-sm ${t.status === "completed" ? "text-slate-400 line-through" : "text-slate-700"}`}>{t.title}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {dialog && <WsDialog dialog={dialog} prod={p} onClose={() => setDialog(null)} pid={pid} onDone={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function WsDialog({ dialog, prod, onClose, pid, onDone }) {
  const [form, setForm] = useState({ request_type: "missing_document", priority: "medium", title: "", why_needed: "", note: "", method: "irs_efile", confirmation_number: "", delivered: false, preparer_id: prod.preparer_id || "", reviewer_id: prod.reviewer_id || "", task: "" });
  const [staff, setStaff] = useState([]);
  useEffect(() => { if (dialog.type === "assign") api.get("/ops/reviewers").then((r) => setStaff(r.data.reviewers)).catch(() => {}); }, [dialog]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    try {
      if (dialog.type === "ir") { if (!form.title.trim()) return toast.error("Title required"); await api.post(`/production/records/${pid}/information-requests`, { request_type: form.request_type, title: form.title, why_needed: form.why_needed, priority: form.priority }); toast.success("Information request sent"); }
      else if (dialog.type === "assign") { await api.post(`/production/records/${pid}/assign`, { preparer_id: form.preparer_id || null, reviewer_id: form.reviewer_id || null }); toast.success("Assignment updated"); }
      else if (dialog.type === "review") { await api.post(`/production/records/${pid}/review`, { action: dialog.action, note: form.note }); toast.success("Done"); }
      else if (dialog.type === "filing") { await api.post(`/production/records/${pid}/filing`, { method: form.method, confirmation_number: form.confirmation_number || null, delivered: form.delivered }); toast.success("Filing recorded"); }
      else if (dialog.type === "task") { if (!form.task.trim()) return toast.error("Title required"); await api.post(`/production/records/${pid}/tasks`, { title: form.task }); toast.success("Task created"); }
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const titles = { ir: "New Information Request", assign: "Assign Team", review: dialog.action === "escalate" ? "Escalate" : "Request Corrections", filing: "File / Deliver", task: "New Task" };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="prod-ws-dialog">
        <DialogHeader><DialogTitle>{titles[dialog.type]}</DialogTitle><DialogDescription className="sr-only">Production action</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {dialog.type === "ir" && (<>
            <select value={form.request_type} onChange={(e) => set("request_type", e.target.value)} data-testid="prod-ir-type" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">{IR_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="prod-ir-title" placeholder="Request title…" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
            <textarea value={form.why_needed} onChange={(e) => set("why_needed", e.target.value)} rows={2} placeholder="Why is this needed? (shown to client)" className="w-full border border-slate-200 rounded-lg p-2.5 text-sm" />
            <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">{["high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}</select>
          </>)}
          {dialog.type === "assign" && (<>
            {[["preparer_id", "Preparer"], ["reviewer_id", "Reviewer"]].map(([k, l]) => (
              <div key={k}><label className="text-xs text-slate-500">{l}</label>
                <select value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`prod-assign-${k}`} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm"><option value="">Unassigned</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            ))}
          </>)}
          {dialog.type === "review" && <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={3} data-testid="prod-review-note" placeholder="Notes…" className="w-full border border-slate-200 rounded-lg p-2.5 text-sm" />}
          {dialog.type === "filing" && (<>
            <select value={form.method} onChange={(e) => set("method", e.target.value)} data-testid="prod-filing-method" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">{["irs_efile", "paper", "fbar", "thai", "advisory_deliverable", "bookkeeping_deliverable", "other"].map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}</select>
            <input value={form.confirmation_number} onChange={(e) => set("confirmation_number", e.target.value)} placeholder="Confirmation # (optional)" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.delivered} onChange={(e) => set("delivered", e.target.checked)} />Mark as delivered</label>
          </>)}
          {dialog.type === "task" && <input value={form.task} onChange={(e) => set("task", e.target.value)} data-testid="prod-task-title" placeholder="Task title…" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-slate-200">Cancel</button>
          <button onClick={submit} data-testid="prod-ws-confirm" className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white font-medium">Confirm</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
